import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { CreditCard, CheckCircle, Clock, Building, Wallet } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { getPayments, getPaymentStatus } from '../lib/api';

export default function PaymentsPage() {
  const { t, language , tx } = useLanguage();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info(tx('Payment cancelled', 'Pago cancelado', 'Payment cancelled'));
    }
    
    fetchPayments();
  }, [searchParams]);

  const fetchPayments = async () => {
    try {
      const response = await getPayments();
      setPayments(response.data);
    } catch (error) {
      toast.error(tx('Failed to fetch payments', 'Error al cargar pagos', 'Failed to fetch payments'));
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info(tx('Payment check timed out', 'Verificación de pago expiró', 'Payment check timed out'));
      return;
    }
    
    setCheckingPayment(true);
    
    try {
      const response = await getPaymentStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        toast.success(tx('Payment successful!', '¡Pago exitoso!', 'Payment successful!'));
        fetchPayments();
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/payments');
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error(tx('Error checking payment', 'Error verificando pago', 'Error checking payment'));
      setCheckingPayment(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={20} weight="fill" className="text-green-500" />;
      case 'pending':
        return <Clock size={20} className="text-amber-500" />;
      default:
        return <Clock size={20} className="text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => `status-${status === 'completed' ? 'paid' : status}`;

  const completedPayments = payments.filter(p => p.status === 'completed');
  const pendingPayments = payments.filter(p => p.status === 'pending');
  const totalRevenue = completedPayments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <Layout title={t('payments')}>
      <div className="space-y-6" data-testid="payments-page">
        {/* Payment Processing Banner */}
        {checkingPayment && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="ship-gradient text-white p-4 rounded-xl flex items-center gap-3"
          >
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{tx('Verifying payment...', 'Verificando pago...', 'Verifying payment...')}</span>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{t('completed')}</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>{completedPayments.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock size={24} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{t('pending')}</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>{pendingPayments.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="kpi-card">
            <div className="flex items-center gap-3">
              <div className="p-3 ship-gradient rounded-xl">
                <CreditCard size={24} className="text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{t('totalRevenue')}</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>${totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Payments Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="kpi-card">
          <h3 className="text-lg font-bold tracking-wide text-gray-900 mb-4" style={{ fontFamily: 'Bebas Neue' }}>
            {t('paymentTransactions')}
          </h3>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4">ID</th>
                    <th className="text-left py-3 px-4">{tx('Invoice', 'Factura', 'Invoice')}</th>
                    <th className="text-left py-3 px-4">{tx('Method', 'Método', 'Méthode')}</th>
                    <th className="text-right py-3 px-4">{t('amount')}</th>
                    <th className="text-left py-3 px-4">{t('status')}</th>
                    <th className="text-left py-3 px-4">{tx('Date', 'Fecha', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{(payment.id || '').slice(0, 8)}...</td>
                      <td className="py-3 px-4 font-mono text-sm">{(payment.invoice_id || payment.invoice_number || '').toString().slice(0, 8)}...</td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-2 text-sm">
                          <CreditCard size={16} />
                          {payment.payment_method}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold">${payment.amount}</td>
                      <td className="py-3 px-4">
                        <span className={`status-badge ${getStatusBadgeClass(payment.status)} flex items-center gap-1.5`}>
                          {getStatusIcon(payment.status)}
                          {payment.status === 'completed' ? (tx('Completed', 'Completado', 'Completed')) : (tx('Pending', 'Pendiente', 'Pending'))}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(payment.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <CreditCard size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">{tx('No payments yet', 'Sin pagos aún', 'No payments yet')}</p>
            </div>
          )}
        </motion.div>

        {/* Payment Methods */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="kpi-card">
          <h3 className="text-lg font-bold tracking-wide text-gray-900 mb-4" style={{ fontFamily: 'Bebas Neue' }}>
            {t('acceptedMethods')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard size={24} className="text-[#0077FF]" />
                <span className="font-semibold">{t('creditDebitCards')}</span>
              </div>
              <p className="text-sm text-gray-600">Visa, Mastercard, American Express</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <Building size={24} className="text-[#0077FF]" />
                <span className="font-semibold">{t('bankTransfer')}</span>
              </div>
              <p className="text-sm text-gray-600">{tx('Direct transfers', 'Transferencia directa', 'Direct transfers')}</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={24} className="text-[#0077FF]" />
                <span className="font-semibold">{tx('Digital Wallets', 'Billeteras Digitales', 'Digital Wallets')}</span>
              </div>
              <p className="text-sm text-gray-600">Apple Pay, Google Pay</p>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
